import { Box } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ConsultationTabOverviewTable } from '@/components/PatientPageV2/ConsultationTabOverviewTable';
import { useConsultationTabOverviewTable } from '@/components/PatientPageV2/hooks/useConsultationTabOverviewTable';
import { ConsultationStatus } from '@/data/constants';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import { usePatientConsultations } from '@/hooks/patients/usePatientConsultations';
import { ConsultationTableColumnHeaders } from '@/types';
import { ConsultDetail } from './ConsultDetail';
import { useConsultHistoryTracking } from './hooks/useConsultHistoryTracking';

export interface ConsultHistoryProps {
  patientId: number;
  scheduledConsultId?: number;
}

const COLUMNS_TO_HIDE = [
  ConsultationTableColumnHeaders.PaymentStatus,
  ConsultationTableColumnHeaders.PaymentType,
  ConsultationTableColumnHeaders.CancelLink,
  ConsultationTableColumnHeaders.RescheduleLink,
];

export const ConsultHistory = ({ patientId, scheduledConsultId }: ConsultHistoryProps) => {
  const [showConsultation, setShowConsultation] = useState<Consultation | null>(null);

  const {
    patientConsultationsData,
    isFetchingPatientConsultations,
    consultationsResponseCursors,
    handlePaginationChange,
    onSort,
    isPatientConsultationsError,
  } = usePatientConsultations(Number(patientId));

  const { trackViewConsultations, trackViewConsultation } = useConsultHistoryTracking();

  const handleSetShowConsultation = useCallback((consultation: Consultation) => {
    setShowConsultation(consultation);
  }, []);

  const { consultationsOverviewTableData, isProcessingConsultations } = useConsultationTabOverviewTable({
    consultations: patientConsultationsData,
    setShowConsultation: handleSetShowConsultation,
    scheduledConsultId: scheduledConsultId,
    onViewClick: trackViewConsultation,
  });

  const prevConsultationIdsRef = useRef<string | null>(null);
  const hasPrevDataRef = useRef(false);

  // Track table data change
  useEffect(() => {
    const hasConsults = consultationsOverviewTableData.length > 0;
    const currentIds = consultationsOverviewTableData.map((c) => c.consultationId).join(',');

    // Skip tracking on first load or if there's no data
    if (!hasConsults || !hasPrevDataRef.current) {
      if (hasConsults) {
        hasPrevDataRef.current = true;
      }
      prevConsultationIdsRef.current = currentIds;
      return;
    }

    const hasChanged = currentIds !== prevConsultationIdsRef.current;

    if (hasChanged) {
      trackViewConsultations(consultationsOverviewTableData);
    }

    prevConsultationIdsRef.current = currentIds;
  }, [consultationsOverviewTableData, trackViewConsultations]);

  const hasSetDefaultConsultation = useRef(false);

  // Open the most recent completed consultation by default
  useEffect(() => {
    if (
      !hasSetDefaultConsultation.current &&
      !showConsultation &&
      patientConsultationsData &&
      patientConsultationsData.length > 0
    ) {
      const mostRecentCompletedConsultation = patientConsultationsData.find(
        (consultation) => consultation?.ConsultationStatus?.id === ConsultationStatus.completed,
      );
      if (mostRecentCompletedConsultation) {
        handleSetShowConsultation(mostRecentCompletedConsultation);
        hasSetDefaultConsultation.current = true;
      }
    }
  }, [patientConsultationsData]);

  const handleCloseConsultation = useCallback(() => {
    setShowConsultation(null);
    trackViewConsultations(consultationsOverviewTableData);
  }, [consultationsOverviewTableData, trackViewConsultations]);

  return (
    <Box>
      <ConsultationTabOverviewTable
        consultations={consultationsOverviewTableData}
        isLoadingConsultations={isFetchingPatientConsultations}
        isProcessingConsultations={isProcessingConsultations}
        isErrorConsultations={isPatientConsultationsError}
        responseCursors={consultationsResponseCursors}
        disabledConsultsCodes={COLUMNS_TO_HIDE}
        onSort={onSort}
        handlePaginationChange={handlePaginationChange}
        scheduledConsultId={scheduledConsultId}
        disableShadow={!!showConsultation}
        hidden={!!showConsultation}
      />

      {showConsultation && (
        <Box aria-hidden={!showConsultation} data-testid="consult-detail">
          <ConsultDetail consultation={showConsultation} onClose={handleCloseConsultation} />
        </Box>
      )}
    </Box>
  );
};
